import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../../forum/app';
import Component from '../../common/Component';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import ItemList from '../../common/utils/ItemList';
import classList from '../../common/utils/classList';
import extractText from '../../common/utils/extractText';
import KeyboardNavigatable from '../../common/utils/KeyboardNavigatable';
import Icon from '../../common/components/Icon';
import DiscussionsSearchSource from './DiscussionsSearchSource';
import UsersSearchSource from './UsersSearchSource';

/**
 * The `SearchSource` interface defines a section of search results in the
 * search dropdown.
 *
 * Search sources should be registered with the `Search` component class
 * by extending the `sourceItems` method. When the user types a
 * query, each search source will be prompted to load search results via the
 * `search` method. When the dropdown is redrawn, it will be constructed by
 * putting together the output from the `view` method of each source.
 */

/**
 * @todo: 2.0 refactored the global search UI and no longer uses this component, now we use the GlobalSearch component.
 *        The component was kept to support extension usage of it on a local scope.
 *        We need to extract this component into a separate UI package instead as it is no longer needed by core.
 *
 * The `Search` component displays a menu of as-you-type results from a variety
 * of sources.
 *
 * The search box will be 'activated' if the app's search state's
 * getInitialSearch() value is a truthy value. If this is the case, an 'x'
 * button will be shown next to the search field, and clicking it will clear the search.
 *
 * ATTRS:
 *
 * - state: SearchState instance.
 */
export default class Search extends Component {
  constructor() {
    super(...arguments);
    /**
     * The instance of `SearchState` for this component.
     */
    _defineProperty(this, "searchState", void 0);
    /**
     * Whether or not the search input has focus.
     */
    _defineProperty(this, "hasFocus", false);
    /**
     * An array of SearchSources.
     */
    _defineProperty(this, "sources", void 0);
    /**
     * The number of sources that are still loading results.
     */
    _defineProperty(this, "loadingSources", 0);
    /**
     * The index of the currently-selected <li> in the results list. This can be
     * a unique string (to account for the fact that an item's position may jump
     * around as new results load), but otherwise it will be numeric (the
     * sequential position within the list).
     */
    _defineProperty(this, "index", 0);
    _defineProperty(this, "navigator", void 0);
    _defineProperty(this, "searchTimeout", void 0);
    _defineProperty(this, "updateMaxHeightHandler", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.searchState = this.attrs.state;
  }
  view() {
    const currentSearch = this.searchState.getInitialSearch();

    // Initialize search sources in the view rather than the constructor so
    // that we have access to app.forum.
    if (!this.sources) this.sources = this.sourceItems().toArray();

    // Hide the search view if no sources were loaded
    if (!this.sources.length) return m("div", null);
    const searchLabel = extractText(app.translator.trans('core.forum.header.search_placeholder'));
    const isActive = !!currentSearch;
    const shouldShowResults = !!(this.searchState.getValue() && this.hasFocus);
    const shouldShowClearButton = !!(!this.loadingSources && this.searchState.getValue());
    return m("div", {
      role: "search",
      "aria-label": app.translator.trans('core.forum.header.search_role_label'),
      className: classList('Search', {
        open: this.searchState.getValue() && this.hasFocus,
        focused: this.hasFocus,
        active: isActive,
        loading: !!this.loadingSources
      })
    }, m("div", {
      className: "Search-input"
    }, m("input", {
      "aria-label": searchLabel,
      className: "FormControl",
      type: "search",
      placeholder: searchLabel,
      value: this.searchState.getValue(),
      oninput: e => this.searchState.setValue(e?.target?.value),
      onfocus: () => this.hasFocus = true,
      onblur: () => this.hasFocus = false
    }), !!this.loadingSources && m(LoadingIndicator, {
      size: "small",
      display: "inline",
      containerClassName: "Button Button--icon Button--link"
    }), shouldShowClearButton && m("button", {
      className: "Search-clear Button Button--icon Button--link",
      onclick: this.clear.bind(this),
      "aria-label": app.translator.trans('core.forum.header.search_clear_button_accessible_label'),
      type: "button"
    }, m(Icon, {
      name: "fas fa-times-circle"
    }))), m("ul", {
      className: "Dropdown-menu Search-results",
      "aria-hidden": !shouldShowResults || undefined,
      "aria-live": shouldShowResults ? 'polite' : undefined
    }, shouldShowResults && this.sources.map(source => source.view(this.searchState.getValue()))));
  }
  updateMaxHeight() {
    // Since extensions might add elements above the search box on mobile,
    // we need to calculate and set the max height dynamically.
    const resultsElementMargin = 14;
    const maxHeight = window.innerHeight - this.element.querySelector('.Search-input>.FormControl').getBoundingClientRect().bottom - resultsElementMargin;
    this.element.querySelector('.Search-results')?.style?.setProperty('max-height', `${maxHeight}px`);
  }
  onupdate(vnode) {
    super.onupdate(vnode);

    // Highlight the item that is currently selected.
    this.setIndex(this.getCurrentNumericIndex());

    // If there are no sources, the search view is not shown.
    if (!this.sources?.length) return;
    this.updateMaxHeight();
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // If there are no sources, we shouldn't initialize logic for
    // search elements, as they will not be shown.
    if (!this.sources?.length) return;
    const search = this;
    const state = this.searchState;

    // Highlight the item that is currently selected.
    this.setIndex(this.getCurrentNumericIndex());
    this.$('.Search-results').on('mousedown', e => e.preventDefault()).on('click', () => this.$('input').trigger('blur'))

    // Whenever the mouse is hovered over a search result, highlight it.
    .on('mouseenter', '> li:not(.Dropdown-header)', function () {
      search.setIndex(search.selectableItems().index(this));
    });
    const $input = this.$('input');
    this.navigator = new KeyboardNavigatable();
    this.navigator.onUp(() => this.setIndex(this.getCurrentNumericIndex() - 1, true)).onDown(() => this.setIndex(this.getCurrentNumericIndex() + 1, true)).onSelect(this.selectResult.bind(this), true).onCancel(this.clear.bind(this)).bindTo($input);

    // Handle input key events on the search input, triggering results to load.
    $input.on('input focus', function () {
      const query = this.value.toLowerCase();
      if (!query) return;
      if (search.searchTimeout) clearTimeout(search.searchTimeout);
      search.searchTimeout = window.setTimeout(() => {
        if (state.isCached(query)) return;
        if (query.length >= search.constructor.MIN_SEARCH_LEN) {
          search.sources?.map(source => {
            if (!source.search) return;
            search.loadingSources++;
            source.search(query).then(() => {
              search.loadingSources = Math.max(0, search.loadingSources - 1);
              m.redraw();
            });
          });
        }
        state.cache(query);
        m.redraw();
      }, 250);
    }).on('focus', function () {
      $(this).one('mouseup', e => e.preventDefault()).trigger('select');
    });
    this.updateMaxHeightHandler = this.updateMaxHeight.bind(this);
    window.addEventListener('resize', this.updateMaxHeightHandler);
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.updateMaxHeightHandler) {
      window.removeEventListener('resize', this.updateMaxHeightHandler);
    }
  }

  /**
   * Navigate to the currently selected search result and close the list.
   */
  selectResult() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.loadingSources = 0;
    const selectedUrl = this.getItem(this.index).find('a').attr('href');
    if (this.searchState.getValue() && selectedUrl) {
      m.route.set(selectedUrl);
    } else {
      this.clear();
    }
    this.$('input').blur();
  }

  /**
   * Clear the search
   */
  clear() {
    this.searchState.clear();
  }

  /**
   * Build an item list of SearchSources.
   */
  sourceItems() {
    const items = new ItemList();
    if (app.forum.attribute('canViewForum')) items.add('discussions', new DiscussionsSearchSource());
    if (app.forum.attribute('canSearchUsers')) items.add('users', new UsersSearchSource());
    return items;
  }

  /**
   * Get all of the search result items that are selectable.
   */
  selectableItems() {
    return this.$('.Search-results > li:not(.Dropdown-header)');
  }

  /**
   * Get the position of the currently selected search result item.
   * Returns zero if not found.
   */
  getCurrentNumericIndex() {
    return Math.max(0, this.selectableItems().index(this.getItem(this.index)));
  }

  /**
   * Get the <li> in the search results with the given index (numeric or named).
   */
  getItem(index) {
    const $items = this.selectableItems();
    let $item = $items.filter(`[data-index="${index}"]`);
    if (!$item.length) {
      $item = $items.eq(index);
    }
    return $item;
  }

  /**
   * Set the currently-selected search result item to the one with the given
   * index.
   */
  setIndex(index, scrollToItem) {
    if (scrollToItem === void 0) {
      scrollToItem = false;
    }
    const $items = this.selectableItems();
    const $dropdown = $items.parent();
    let fixedIndex = index;
    if (index < 0) {
      fixedIndex = $items.length - 1;
    } else if (index >= $items.length) {
      fixedIndex = 0;
    }
    const $item = $items.removeClass('active').eq(fixedIndex).addClass('active');
    this.index = parseInt($item.attr('data-index')) || fixedIndex;
    if (scrollToItem) {
      const dropdownScroll = $dropdown.scrollTop();
      const dropdownTop = $dropdown.offset().top;
      const dropdownBottom = dropdownTop + $dropdown.outerHeight();
      const itemTop = $item.offset().top;
      const itemBottom = itemTop + $item.outerHeight();
      let scrollTop;
      if (itemTop < dropdownTop) {
        scrollTop = dropdownScroll - dropdownTop + itemTop - parseInt($dropdown.css('padding-top'), 10);
      } else if (itemBottom > dropdownBottom) {
        scrollTop = dropdownScroll - dropdownBottom + itemBottom + parseInt($dropdown.css('padding-bottom'), 10);
      }
      if (typeof scrollTop !== 'undefined') {
        $dropdown.stop(true).animate({
          scrollTop
        }, 100);
      }
    }
  }
}
/**
 * The minimum query length before sources are searched.
 */
_defineProperty(Search, "MIN_SEARCH_LEN", 3);
flarum.reg.add('core', 'forum/components/Search', Search);